<template>
  <div
    class="flex flex-col gap-2 text-left"
    data-testid="coverage-mode-selector"
    @mouseleave="onHoverEnd"
    @focusout="onFocusOut"
  >
    <span v-if="label" class="station-form-category-title">{{ label }}</span>

    <div
      v-if="variant === 'segmented'"
      role="radiogroup"
      :aria-label="label"
      class="flex w-full rounded-lg border border-solid border-gray-300 overflow-hidden"
    >
      <button
        v-for="option in renderedOptions"
        :key="option"
        type="button"
        role="radio"
        :aria-checked="modelValue === option"
        :disabled="disabled"
        v-bind="lockedAttrs(option)"
        :data-testid="`coverage-mode-option-${option.toLowerCase()}`"
        class="flex-1 px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          isLocked(option)
            ? 'coverage-option-locked inline-flex items-center justify-center gap-1.5'
            : modelValue === option
              ? 'bg-primary text-white'
              : 'bg-white text-textPrimary hover:bg-primary5'
        "
        v-on="lockedListeners(option)"
        @click="select(option)"
      >
        <svg
          v-if="isLocked(option)"
          class="coverage-lock-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          data-testid="coverage-mode-lock-icon"
        >
          <path :d="mdiLock" />
        </svg>
        {{ COVERAGE_MODE_LABELS[option] }}
      </button>
    </div>

    <div v-else role="radiogroup" :aria-label="label" class="grid grid-cols-1 md:grid-cols-2 gap-3">
      <button
        v-for="option in renderedOptions"
        :key="option"
        type="button"
        role="radio"
        :aria-checked="modelValue === option"
        :disabled="disabled"
        v-bind="lockedAttrs(option)"
        :data-testid="`coverage-mode-option-${option.toLowerCase()}`"
        class="rounded-lg border border-solid p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          isLocked(option)
            ? 'coverage-option-locked border-lightGray30'
            : modelValue === option
              ? 'border-primary bg-primary5'
              : 'border-lightGray30 bg-white'
        "
        v-on="lockedListeners(option)"
        @click="select(option)"
      >
        <span class="flex items-center justify-between gap-2">
          <span
            class="inline-flex items-center gap-1.5 font-bold text-sm"
            :class="isLocked(option) ? '' : 'text-textPrimary'"
          >
            <svg
              v-if="isLocked(option)"
              class="coverage-lock-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              data-testid="coverage-mode-lock-icon"
            >
              <path :d="mdiLock" />
            </svg>
            {{ CARD_TITLES[option] }}
          </span>
          <span class="text-xs rounded px-2 py-0.5" :class="CARD_BADGE_CLASSES[option]">
            {{ CARD_BADGES[option] }}
          </span>
        </span>
        <span class="block text-xs text-[#68688D] mt-1">{{ CARD_DESCRIPTIONS[option] }}</span>
      </button>
    </div>

    <!-- Explicação do segmento desativado: painel ancorado logo abaixo do seletor (sem
         position:absolute, para não ser cortado pelo overflow dos modais). -->
    <div
      v-if="lockInfoOpen"
      :id="lockInfoId"
      role="tooltip"
      class="coverage-locked-info rounded-lg px-3 py-2 text-sm"
      data-testid="coverage-mode-unidentified-locked-info"
      @mouseenter="hovering = true"
    >
      <p class="flex items-center gap-1.5 font-bold text-textPrimary">
        <svg class="coverage-lock-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path :d="mdiLock" />
        </svg>
        Opção desativada nesta escala
      </p>
      <p class="mt-1 text-[#3E3E5A]">
        Para marcar plantões cobertos por equipe da unidade, servidor ou outra empresa sem indicar
        o médico, a opção precisa estar ligada na configuração da escala.
      </p>
      <p class="mt-1 text-xs text-[#68688D]" data-testid="coverage-mode-locked-steps">
        Gerenciar escala → Editar escala → Informações básicas → ligar 'Permite plantão ocupado sem
        identificação'
      </p>
      <button
        v-if="canOpenScheduleSettings"
        type="button"
        class="mt-2 text-sm font-bold text-primary30 underline"
        data-testid="coverage-mode-open-schedule-settings"
        @click="emit('openScheduleSettings')"
      >
        Abrir configuração da escala
      </button>
      <p v-else class="mt-2 text-xs font-semibold text-[#3E3E5A]">
        Peça a quem administra a escala para ligar.
      </p>
    </div>

    <p
      v-if="variant === 'segmented' && modelValue === CoverageMode.UNIDENTIFIED"
      class="text-xs text-[#68688D]"
      data-testid="coverage-mode-unidentified-hint"
    >
      Sem médico no plantão: não há check-in, Registro de Atividade, pós-plantão nem pagamento.
      Formação, "Médico escalado por" e a checagem de horas consecutivas não se aplicam.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiLock } from '@mdi/js'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import { COVERAGE_MODE_LABELS } from '@/utils/coverage'

const props = withDefaults(
  defineProps<{
    modelValue: CoverageMode
    allowUnidentified: boolean
    options?: CoverageMode[]
    variant?: 'segmented' | 'cards'
    label?: string
    disabled?: boolean
    /** BE com o recurso e escala desligada: mostra o segmento desativado com a explicação. */
    unidentifiedLocked?: boolean
    /** Quem tem `schedule_editing` na escala vê o atalho para a configuração. */
    canOpenScheduleSettings?: boolean
  }>(),
  {
    options: () => [CoverageMode.DOCTOR, CoverageMode.UNIDENTIFIED, CoverageMode.OPEN],
    variant: 'segmented',
    label: 'Cobertura do plantão',
    disabled: false,
    unidentifiedLocked: false,
    canOpenScheduleSettings: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: CoverageMode]
  openScheduleSettings: []
}>()

const CARD_TITLES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'Médico',
  [CoverageMode.UNIDENTIFIED]: 'Ocupado sem identificação',
  [CoverageMode.OPEN]: 'Vaga aberta'
}
const CARD_DESCRIPTIONS: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'Um médico cadastrado cobre o plantão.',
  [CoverageMode.UNIDENTIFIED]: 'Já está coberto por terceiro. Não é vaga e nada é pago.',
  [CoverageMode.OPEN]: 'Precisa de médico. Entra nas métricas e nas ações de vaga.'
}
const CARD_BADGES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'Com médico',
  [CoverageMode.UNIDENTIFIED]: 'Ocupado',
  [CoverageMode.OPEN]: 'Aberta'
}
const CARD_BADGE_CLASSES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'bg-primary5 text-primary40',
  [CoverageMode.UNIDENTIFIED]: 'coverage-badge-unidentified text-[#3E3E5A]',
  [CoverageMode.OPEN]: 'bg-[var(--st-open-bg,#FFEDD5)] text-[color:var(--st-open-text,#F18F1B)]'
}

const visibleOptions = computed(() =>
  props.options.filter((option) => option !== CoverageMode.UNIDENTIFIED || props.allowUnidentified)
)
const isLocked = (option: CoverageMode) =>
  option === CoverageMode.UNIDENTIFIED && !props.allowUnidentified && props.unidentifiedLocked
// Renderizadas = selecionáveis + a desativada (visível, mas nunca selecionada).
const renderedOptions = computed(() =>
  props.options.filter((option) => visibleOptions.value.includes(option) || isLocked(option))
)

const lockInfoId = `coverage-locked-info-${Math.random().toString(36).slice(2, 10)}`
const hovering = ref(false)
const focused = ref(false)
const pinned = ref(false)
const lockInfoOpen = computed(() => hovering.value || focused.value || pinned.value)

function closeLockInfo() {
  hovering.value = false
  focused.value = false
  pinned.value = false
}
function onHoverEnd() {
  hovering.value = false
}
function onFocusOut(event: FocusEvent) {
  const next = event.relatedTarget as Node | null
  const root = event.currentTarget as HTMLElement | null
  if (next && root?.contains(next)) return
  focused.value = false
  pinned.value = false
}

// `aria-disabled` (não `disabled`): o segmento continua focável e o aviso abre pelo teclado.
const lockedAttrs = (option: CoverageMode) =>
  isLocked(option)
    ? {
        'aria-disabled': 'true' as const,
        'aria-describedby': lockInfoOpen.value ? lockInfoId : undefined
      }
    : {}
const lockedListeners = (option: CoverageMode) =>
  isLocked(option)
    ? {
        mouseenter: () => (hovering.value = true),
        focus: () => (focused.value = true),
        keydown: (event: KeyboardEvent) => {
          if (event.key === 'Escape') closeLockInfo()
        }
      }
    : {}

function select(option: CoverageMode) {
  if (isLocked(option)) {
    pinned.value = !pinned.value
    if (!pinned.value) closeLockInfo()
    return
  }
  if (props.disabled || option === props.modelValue) return
  emit('update:modelValue', option)
}

// Troca de escala (ou flag desligada) com "Ocupado" marcado: volta para o que é permitido.
watch(
  visibleOptions,
  (options) => {
    if (options.length && !options.includes(props.modelValue)) emit('update:modelValue', options[0])
  },
  { immediate: true }
)
</script>

<style scoped>
.coverage-option-locked {
  background: #f3f3f6;
  color: #8e8ea8;
  cursor: not-allowed;
}
.coverage-option-locked:focus-visible {
  outline: 2px solid #2f7f91;
  outline-offset: -2px;
}
.coverage-lock-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  fill: currentColor;
}
.coverage-locked-info {
  background: #f7f7fa;
  border: 1px solid #bcbccd;
}
.coverage-badge-unidentified {
  background: repeating-linear-gradient(135deg, #eeeef3 0 5px, #e1e1ea 5px 10px);
  border: 1px solid #bcbccd;
}
</style>
